
import { Link } from '@tanstack/react-router';

import { Activity, Building, Crosshair, Database, FolderOpen, Headphones, Shield, Users } from 'lucide-react';
import {motion} from 'motion/react';

const FONT_FAMILY = '"Inter", system-ui, sans-serif';

const modules = [
  {
    icon: Shield,
    name: 'GRC',
    description: 'Governance, Risk & Compliance',
    color: '#1e638d',
    link: '/platform/grc',
  },
  {
    icon: Users,
    name: 'IAM',
    description: 'Identity & Access Management',
    color: '#2563eb',
    link: '/platform/iam',
  },
  {
    icon: Database,
    name: 'CMDB',
    description: 'Configuration Management',
    color: '#0891b2',
    link: '/platform/cmdb',
  },
  {
    icon: Activity,
    name: 'XDR',
    description: 'Extended Detection & Response',
    color: '#7c3aed',
    link: '/platform/xdr',
  },
  {
    icon: Headphones,
    name: 'Service Desk',
    description: 'IT Service Management',
    color: '#059669',
    link: '/platform/service-desk',
  },
  {
    icon: Crosshair,
    name: 'Penetration Testing',
    description: 'Security Assessments',
    color: '#dc2626',
    link: '/platform/pentest',
  },
  {
    icon: FolderOpen,
    name: 'Documents',
    description: 'AI-Powered Document Management',
    color: '#ea580c',
    link: '/platform/documents',
  },
  {
    icon: Building,
    name: 'Business',
    description: 'Entity & Process Management',
    color: '#4f46e5',
    link: '/platform/business',
  },
];

const complianceFrameworks = [
  'NIST CSF',
  'ISO 27001',
  'SOC 2',
  'HIPAA',
  'GDPR',
  'PCI DSS',
  'FFIEC',
  'CMMC',
];

export default function BOSSSProductSection() {
  return (
    <motion.div
      animate={{opacity: 1, y: 0}}
      initial={{opacity: 0, y: 40}}
      className="text-center mb-12 sm:mb-16"
      transition={{duration: 1, delay: 0.3, ease: 'easeOut'}}
    >
      {/* Glowing background effect */}
      <div className="relative mb-4 sm:mb-6 mt-0">
        <motion.div
          className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[80vw] max-w-[600px] h-24 sm:h-32 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          style={{
            background:
              'radial-gradient(ellipse, rgba(30, 99, 141, 0.2) 0%, rgba(30, 99, 141, 0.1) 50%, transparent 70%)',
          }}
        />

        <h2
          className="relative z-10 font-bold tracking-tight"
          style={{
            fontFamily: FONT_FAMILY,
            fontSize: 'clamp(2.5rem, 7vw, 5rem)',
            background:
              'linear-gradient(135deg, #1e638d 0%, #2563eb 50%, #3b82f6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter:
              'drop-shadow(0 0 20px rgba(30, 99, 141, 0.4))',
            textShadow: '0 0 40px rgba(30, 99, 141, 0.3)',
          }}
        >
          BOSSS
        </h2>

        {/* Subtle acronym expansion */}
        <motion.p
          animate={{opacity: 1}}
          initial={{opacity: 0}}
          style={{ fontFamily: FONT_FAMILY }}
          transition={{ duration: 0.6, delay: 2.0 }}
          className="text-xs sm:text-sm text-gray-400 tracking-widest uppercase mt-2"
        >
          Back Office Security Support System
        </motion.p>
      </div>

      {/* Value Proposition Headline */}
      <motion.h1
        animate={{opacity: 1, y: 0}}
        initial={{opacity: 0, y: 20}}
        className="text-center max-w-4xl mx-auto px-4 mb-4"
        transition={{
          duration: 0.8,
          delay: 2.0,
          ease: 'easeOut',
        }}
        style={{
          fontFamily: FONT_FAMILY,
          fontSize: 'clamp(1.5rem, 4vw, 2.5rem)',
          fontWeight: '700',
          color: '#1e293b',
          lineHeight: '1.2',
        }}
      >
        The Cybersecurity ERP — One System for Your Entire Security Operation.
      </motion.h1>

      {/* Benefit Statement */}
      <motion.p
        animate={{opacity: 1, y: 0}}
        initial={{opacity: 0, y: 20}}
        className="text-center max-w-3xl mx-auto px-4 mb-10"
        transition={{
          duration: 0.8,
          delay: 2.2,
          ease: 'easeOut',
        }}
        style={{
          fontFamily: FONT_FAMILY,
          fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
          fontWeight: '400',
          color: '#64748b',
          lineHeight: '1.6',
        }}
      >
        Replace 8–12 disconnected security tools with one unified system. BOSSS connects your risks, assets, identities, threats, compliance, and AI in a single living platform—so every team always works from the same picture.
      </motion.p>

      {/* Four Module Preview */}
      <motion.div
        animate={{opacity: 1, y: 0}}
        initial={{opacity: 0, y: 30}}
        className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3 sm:gap-4 max-w-5xl mx-auto px-4 mb-10"
        transition={{
          duration: 0.8,
          delay: 2.4,
          ease: 'easeOut',
        }}
      >
        {modules.map((module, index) => (
          <Link
            to={module.link}
            key={module.name}
            className="block"
            aria-label={`Navigate to ${module.name} product page`}
          >
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-gray-100 shadow-sm transition-all h-full"
              whileHover={{
                scale: 1.05,
                boxShadow: '0 10px 40px rgba(30, 99, 141, 0.2)',
              }}
              transition={{
                duration: 0.5,
                delay: 2.5 + index * 0.1,
                ease: 'easeOut',
              }}
            >
              <div
                style={{ backgroundColor: `${module.color}15` }}
                className="w-12 h-12 rounded-lg mx-auto mb-3 flex items-center justify-center"
              >
                <module.icon
                  className="w-6 h-6"
                  style={{ color: module.color }}
                />
              </div>

              <h3
                style={{ color: module.color }}
                className="font-bold text-lg mb-1"
              >
                {module.name}
              </h3>

              <p className="text-xs text-gray-500 leading-tight">
                {module.description}
              </p>
            </motion.div>
          </Link>
        ))}
      </motion.div>

      {/* Compliance Ready Strip */}
      <motion.div
        animate={{opacity: 1}}
        initial={{opacity: 0}}
        className="flex flex-col items-center gap-3 mb-8"
        transition={{
          duration: 0.8,
          delay: 2.8,
          ease: 'easeOut',
        }}
      >
        <p className="text-sm text-gray-400 uppercase tracking-wider font-medium">
          Regulatory Aligned
        </p>

        <div className="flex flex-wrap justify-center gap-3">
          {complianceFrameworks.map((framework, index) => (
            <motion.span
              key={framework}
              animate={{ opacity: 1, scale: 1 }}
              initial={{ opacity: 0, scale: 0.8 }}
              className="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 border border-gray-200"
              transition={{
                duration: 0.3,
                delay: 2.9 + index * 0.05,
              }}
            >
              {framework}
            </motion.span>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}
